/**
 * Page Guide Utilisateur - Téléchargement du PDF
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Download, 
  Eye, 
  FileText, 
  HelpCircle,
  Users,
  Target,
  BarChart3,
  Settings,
  Upload,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserGuidePage() {
  const { isAuthenticated } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/docs/user-guide', { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Guide_Utilisateur_HR_Engine.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Guide téléchargé avec succès !');
    } catch (error) {
      console.error('Error downloading guide:', error);
      toast.error('Erreur lors du téléchargement du guide');
    } finally {
      setDownloading(false);
    }
  };

  const handlePreviewPDF = async () => {
    setPreviewing(true);
    try {
      const response = await api.get('/docs/user-guide/preview', { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error previewing guide:', error);
      toast.error('Erreur lors de la prévisualisation');
    } finally {
      setPreviewing(false);
    }
  };

  const guideModules = [
    {
      title: 'Module Organisation',
      icon: Users,
      description: 'Gestion des employés, trombinoscope, organigramme, fiches de poste',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Module Performance',
      icon: Target,
      description: 'Campagnes d\'évaluation, auto-évaluation, évaluation manager, validation RH',
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Module Talents',
      icon: Sparkles,
      description: 'Bibliothèque de compétences, plans de développement',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Reporting & Analyse',
      icon: BarChart3,
      description: 'KPIs RH, insights IA, tableaux de bord analytiques',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      title: 'Administration',
      icon: Settings,
      description: 'Paramètres, départements, sites, utilisateurs',
      color: 'bg-gray-100 text-gray-600'
    },
    {
      title: 'Import de Données',
      icon: Upload,
      description: 'Import Excel/CSV des employés et données RH',
      color: 'bg-indigo-100 text-indigo-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <BookOpen className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Guide Utilisateur</h1>
              <p className="text-gray-500 mt-1">Documentation complète de Webtech HR Engine™</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Colonne principale - Téléchargement */}
          <div className="lg:col-span-2 space-y-6">
            {/* Carte principale */}
            <Card className="border-2 border-blue-100 bg-gradient-to-br from-blue-50/50 to-white">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-600" />
                  <div>
                    <CardTitle>Guide Utilisateur PDF</CardTitle>
                    <CardDescription>
                      Documentation complète de A à Z avec captures d'écran
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-gray-600">
                  Ce guide couvre l'ensemble des fonctionnalités de la plateforme HR Engine, 
                  incluant la gestion des employés, les campagnes d'évaluation, le reporting RH 
                  et l'administration de votre espace entreprise.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={handleDownloadPDF} 
                    disabled={downloading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="download-guide-btn"
                  >
                    {downloading ? (
                      <>
                        <Skeleton className="h-4 w-4 mr-2 rounded-full animate-spin" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger le PDF
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    onClick={handlePreviewPDF} 
                    disabled={previewing}
                    variant="outline"
                    className="flex-1"
                    data-testid="preview-guide-btn"
                  >
                    {previewing ? (
                      <>
                        <Skeleton className="h-4 w-4 mr-2 rounded-full animate-spin" />
                        Chargement...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Prévisualiser
                      </>
                    )}
                  </Button>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Besoin d'aide supplémentaire ?</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Contactez notre support à <a href="mailto:support@webtech-sarl.com" className="underline">support@webtech-sarl.com</a>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Modules couverts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Modules couverts dans le guide</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {guideModules.map((module, index) => {
                    const Icon = module.icon;
                    return (
                      <div 
                        key={index}
                        className="flex items-start gap-3 p-4 rounded-lg border bg-gray-50/50 hover:bg-gray-50 transition-colors"
                      >
                        <div className={`p-2 rounded-lg ${module.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{module.title}</h4>
                          <p className="text-sm text-gray-500 mt-1">{module.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colonne latérale - Sommaire */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Table des matières</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { num: '1', title: 'Introduction' },
                  { num: '2', title: 'Connexion et Navigation' },
                  { num: '3', title: 'Tableau de Bord' },
                  { num: '4', title: 'Module Organisation' },
                  { num: '5', title: 'Module Performance' },
                  { num: '6', title: 'Module Talents' },
                  { num: '7', title: 'Administration' },
                  { num: '8', title: 'Annexes' }
                ].map((item) => (
                  <div key={item.num} className="flex items-center gap-3 py-2 border-b last:border-0">
                    <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs">
                      {item.num}
                    </Badge>
                    <span className="text-sm text-gray-700">{item.title}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-blue-600 text-white">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="p-3 bg-white/20 rounded-full w-fit mx-auto">
                    <BookOpen className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Version 2.0</h3>
                    <p className="text-sm text-blue-100 mt-1">
                      Guide mis à jour avec toutes les dernières fonctionnalités
                    </p>
                  </div>
                  <Badge className="bg-white text-blue-600">
                    ~20 pages
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
